const uuid = require('uuid')
const RawExternalDataPGAdapter = require('../../common/raw-external-pg-adapter')
const PGAdapter = require('../../common/pg-adapter')
const DataOrderAdapter = require('../../order/data-access/data-order-pg-adapter')
const DataOrderLineAdapter = require('../../order/data-access/data-orderline-pg-adapter')
const LocationOptionsPgAdapter = require('../../location/data-access/location-options-pg-adapter')
const { normalizeProgram, SALES_ORDER_PROGRAM_MAP, PROGRAM_PREFIX_TO_SERVICE_FSID } = require('../tools/normalize-program')

const RETURNS_PROGRAM = 'ZRRE'

async function resolveServiceIdsByProgram (pgConnection, programs) {
  const fsids = [...new Set(
    programs.map(p => {
      const mapped = SALES_ORDER_PROGRAM_MAP[p]
      const normalized = mapped && normalizeProgram(mapped)
      return normalized ? PROGRAM_PREFIX_TO_SERVICE_FSID[normalized] : null
    }).filter(Boolean)
  )]
  if (!fsids.length) return new Map()
  const { rows } = await pgConnection.query(
    'SELECT id, fsid FROM avocado.data_service WHERE fsid = ANY($1)',
    [fsids]
  )
  return new Map(rows.map(r => [r.fsid, r.id]))
}

function shouldSkipLine (line) {
  return (
    line.program === RETURNS_PROGRAM ||
    String(line.line_no) === '0' ||
    line.status === 'not relevant'
  )
}

class SalesOrdersSyncAdapter {
  constructor ({ pgConnection, logger, user }) {
    this.pgConnection = pgConnection
    this.logger = logger
    this.user = user

    this.rawOrdersAdapter = new RawExternalDataPGAdapter({
      pgConnection,
      username: user,
      logger,
      tableName: 'raw.external_sale_event',
      extraColumns: ['source_event_id']
    })

    this.orderAdapter = new DataOrderAdapter(pgConnection, user, logger)
    this.orderLineAdapter = new DataOrderLineAdapter(pgConnection, user, logger)
    this.locationOptionsAdapter = new LocationOptionsPgAdapter({ pgConnection, username: user, logger })

    this.saleEventAdapter = new PGAdapter(
      pgConnection,
      'avocado.data_ext_saleevent',
      user,
      ['id', 'location_id', 'sku', 'date', 'sold', 'period_start_date', 'remark',
        'source', 'organisation_id', 'service_id', 'sale_price', 'item_unique_id_at_source',
        'created_at', 'updated_at', 'created_by', 'updated_by'],
      'id', {}, logger
    )
  }

  /**
   * Dump each flat CSV order line as one raw row.
   */
  async syncRawOrders (organisationId, salesOrdersData, options = {}) {
    const sourceEventId = uuid.v4()

    const dataToStore = salesOrdersData.items
      .filter(line => line.delivery_date)
      .map(line => ({
        organisation_id: organisationId,
        data: line,
        source_event_id: sourceEventId
      }))

    const savedRecords = await this.rawOrdersAdapter.upsertBatchWithJsonbPaths(dataToStore, {
      conflictIdColumns: ['organisation_id'],
      conflictJsonbPaths: [
        { column: 'data', key: 'order_no' },
        { column: 'data', key: 'line_no' },
        { column: 'data', key: 'delivery_date' }
      ],
      // Only rewrite when the line payload actually changed; identical daily re-dumps are skipped.
      updateWhereDistinct: ['data'],
      batchSize: 2000,
      client: options.client
    })

    return { total: savedRecords.length, sourceEventId }
  }

  /**
   * Find the orders this run touched, then return ALL of their lines so
   * duplicate-SKU merges sum over every line, not just the re-ingested ones.
   */
  async getOrdersToTransform (organisationId, options = {}) {
    const changedRecords = await this.rawOrdersAdapter.getByOrganisation(organisationId, null, {
      client: options.client,
      filters: options.sourceEventId ? { source_event_id: options.sourceEventId } : {}
    })
    const orderNos = [...new Set(
      changedRecords.map(r => r.data.order_no).filter(Boolean)
    )]
    if (orderNos.length === 0) return []

    const client = options.client || this.pgConnection
    const { rows } = await client.query(
      `SELECT * FROM raw.external_sale_event
       WHERE organisation_id = $1
         AND data->>'order_no' = ANY($2)
       ORDER BY created_at DESC`,
      [organisationId, orderNos]
    )
    return rows
  }

  /**
   * Read from raw.external_sale_event and transform into
   * data_order / data_orderline shaped records.
   */
  async transformRawOrders (organisationId, options = {}) {
    const rawRecords = await this.getOrdersToTransform(organisationId, options)

    // Resolve all customerCodes -> SDP fsid in a single query
    const customerCodes = [...new Set(
      rawRecords.map(r => r.data.customer_code).filter(Boolean)
    )]
    const locationRows = await this.locationOptionsAdapter.getLocationsByWarehouseCodes(customerCodes)
    const fsidByCode = Object.fromEntries(locationRows.map(r => [r.warehouse_code, r.fsid]))

    const orders = []
    const linesByOrderNo = new Map()
    const unresolvedCustomerCodes = []

    // Each raw record is one flat CSV line; group lines back into orders by order_no.
    for (const record of rawRecords) {
      const line = record.data
      const orderNo = line.order_no
      const destinationId = fsidByCode[line.customer_code]

      if (shouldSkipLine(line)) continue

      // Skip lines whose customerCode does not resolve to an SDP location
      if (!destinationId) {
        unresolvedCustomerCodes.push({ orderNo, customerCode: line.customer_code })
        continue
      }

      if (!linesByOrderNo.has(orderNo)) {
        const mappedProgram = SALES_ORDER_PROGRAM_MAP[line.program]
        if (!mappedProgram) {
          this.logger.warn('Unknown sales order program mapping', { orderNo, program: line.program })
        }
        const normalizedProgram = mappedProgram && normalizeProgram(mappedProgram)
        const programPrefix = normalizedProgram && normalizedProgram.toLowerCase()
        const programId = programPrefix ? `program:${programPrefix}` : null
        const funderId = programPrefix ? `funder:${programPrefix}` : null

        linesByOrderNo.set(orderNo, [])
        orders.push({
          id: uuid.v4(),
          order_id: orderNo,
          sub_order_id: orderNo,
          group_id: orderNo,
          order_type: 'sales',
          status: line.status,
          destination_id: destinationId,
          program_id: programId,
          funder_id: funderId,
          is_complete: line.status === 'complete',
          delivery_date: line.delivery_date
        })
      }

      const sku = line.npc_code
      const original = Math.round(Number(line.qty_ordered) || 0)
      const received = Math.round(Number(line.qty_delivered) || 0)

      // Merge duplicate SKUs within an order so the (order_id, sku) upsert
      // doesn't try to touch the same conflict target twice.
      const lines = linesByOrderNo.get(orderNo)
      const existingLine = lines.find(l => l.sku === sku)
      if (existingLine) {
        existingLine.original += original
        existingLine.adjusted += original
        existingLine.received += received
      } else {
        lines.push({ sku, original, adjusted: original, received })
      }
    }

    if (unresolvedCustomerCodes.length > 0) {
      this.logger.warn('Skipping sales order lines: unresolved customer codes', { unresolvedCustomerCodes })
    }

    return { orders, linesByOrderNo }
  }

  /**
   * Upsert transformed orders and lines into data_order / data_orderline.
   */
  async syncTransformedOrders (organisationId, options = {}) {
    const { orders, linesByOrderNo } = await this.transformRawOrders(organisationId, options)

    if (orders.length === 0) {
      this.logger.info('No sales orders to sync', { organisationId })
      return
    }

    const savedOrders = await this.orderAdapter.bulkUpsert(orders, {
      conflictIdColumns: ['order_id'],
      excludeOnUpdate: ['id'],
      batchSize: 2000,
      client: options.client
    })

    const idByOrderNo = Object.fromEntries(
      savedOrders.map(r => [r.order_id, r.id])
    )

    const orderLines = orders.flatMap(order => {
      const internalId = idByOrderNo[order.order_id]
      return linesByOrderNo.get(order.order_id).map(line => ({
        order_id: internalId,
        sku: line.sku,
        original: line.original,
        adjusted: line.adjusted,
        received: line.received
      }))
    })

    await this.orderLineAdapter.bulkUpsert(orderLines, {
      conflictIdColumns: ['order_id', 'sku'],
      batchSize: 2000,
      client: options.client
    })

    this.logger.info('Successfully synced sales orders', {
      organisationId,
      totalOrders: orders.length,
      totalLines: orderLines.length
    })
  }

  /**
   * Read from raw.external_sale_event and
   * transform each line into a data_ext_saleevent record.
   */
  async transformRawSaleEvents (organisationId, options = {}) {
    const rawRecords = await this.rawOrdersAdapter.getByOrganisation(organisationId, null, {
      client: options.client,
      filters: options.sourceEventId ? { source_event_id: options.sourceEventId } : {}
    })

    const plantCodes = [...new Set(rawRecords.map(r => r.data.plant).filter(Boolean))]
    const programs = [...new Set(rawRecords.map(r => r.data.program).filter(Boolean))]

    const [locationRows, serviceIdByFsid] = await Promise.all([
      this.locationOptionsAdapter.getLocationsByWarehouseCodes(plantCodes),
      resolveServiceIdsByProgram(this.pgConnection, programs)
    ])
    const locationUuidByPlant = Object.fromEntries(locationRows.map(r => [r.warehouse_code, r.location_id]))

    const rowsByUniqueId = new Map()
    const unresolvedPlants = []
    for (const record of rawRecords) {
      const line = record.data
      const locationId = locationUuidByPlant[line.plant]

      if (shouldSkipLine(line)) continue

      if (!locationId) {
        unresolvedPlants.push({ orderNo: line.order_no, plant: line.plant })
        continue
      }

      const mapped = SALES_ORDER_PROGRAM_MAP[line.program]
      const normalized = mapped && normalizeProgram(mapped)
      const serviceFsid = normalized ? PROGRAM_PREFIX_TO_SERVICE_FSID[normalized] : null
      const serviceId = serviceFsid ? serviceIdByFsid.get(serviceFsid) : null

      const itemUniqueIdAtSource = `${line.order_no}-${line.line_no}-${line.delivery_date}`
      rowsByUniqueId.set(itemUniqueIdAtSource, {
        id: uuid.v4(),
        location_id: locationId,
        sku: line.npc_code,
        sold: Math.round(Number(line.qty_delivered) || 0),
        sale_price: Number(line.unit_price) || 0,
        date: line.delivery_date,
        period_start_date: line.delivery_date,
        service_id: serviceId,
        source: options.sourceEventId,
        organisation_id: organisationId,
        item_unique_id_at_source: itemUniqueIdAtSource
      })
    }

    if (unresolvedPlants.length > 0) {
      this.logger.warn('Skipping sale event lines: unresolved plants', { unresolvedPlants })
    }

    return [...rowsByUniqueId.values()]
  }

  /**
   * Transform raw sale events and upsert them into data_ext_saleevent.
   */
  async syncSaleEvents (organisationId, options = {}) {
    const rows = await this.transformRawSaleEvents(organisationId, options)

    if (rows.length === 0) {
      this.logger.info('No sale events to sync', { organisationId })
      return { total: 0 }
    }

    await this.saleEventAdapter.bulkUpsert(rows, {
      conflictIdColumns: ['item_unique_id_at_source', 'organisation_id'],
      batchSize: 500,
      client: options.client
    })

    this.logger.info('Successfully synced sale events', {
      organisationId,
      total: rows.length
    })

    return { total: rows.length }
  }
}

module.exports = SalesOrdersSyncAdapter
